import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";
import { CouponsFormValues } from "src/models/Coupons.model";
import CouponForm from "../Layouts/CouponForm";
import {
  useGetCouponByIdQuery,
  useUpdateCouponByIdMutation,
} from "src/services/CouponService";

type Props = {
  onClose: () => void;
  selectedCouponId: string;
};

const EditCouponWrapper = ({ onClose, selectedCouponId }: Props) => {
  const [items, setItems] = useState<any>({});

  const { isLoading, isFetching, data } =
    useGetCouponByIdQuery(selectedCouponId);
  const [updateCouponById] = useUpdateCouponByIdMutation();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setItems(data?.data);
    }
  }, [isLoading, isFetching, data]);

  // Form Initial Values

  const initialValues: CouponsFormValues = {
    couponCode: items?.couponCode || "",
    minOrderAmount: items?.minOrderAmount || "",
    discountType: { label: items?.discountType, value: items?.discountType },
    discountValue: items?.discountValue || "0",
    startsDate: items?.startsDate || "",
    expiryDate: items?.expiryDate || "",
    product: items?.product?.map((p: any) => ({
      label: p.productName,
      value: p.productId,
    })),
    maxCount: items?.maxCount || "",
    student: items?.student?.map((s: any) => ({
      label: s.name,
      value: s.userId,
    })),
  };
  // Validation Schema
  const validationSchema = object().shape({
    couponCode: string().required("Please enter coupon code"),
    minOrderAmount: string().required("Please enter minimum order amount"),
    discountType: object().required("Please select discount type"),
    discountValue: string().required("Please enter discount value"),
    startsDate: string().required("Please enter start date"),
    maxCount: string().required("Please enter max count"),
  });

  // Handle Submit
  const handleSubmit = (
    values: CouponsFormValues,
    { setSubmitting, resetForm }: FormikHelpers<CouponsFormValues>
  ) => {
    let productPayload = {};
    if (values.product?.length) {
      productPayload = {
        product: values.product.map((p: any) => p.value),
        isForAllProduct: false,
      };
    } else {
      productPayload = {
        isForAllProduct: true,
      };
    }
    let studentPayload = {};
    if (values.student?.length) {
      studentPayload = {
        student: values.student.map((s: any) => s.value),
        isForAllStudent: false,
      };
    } else {
      studentPayload = {
        isForAllStudent: true,
      };
    }
    const payload = {
      couponCode: values.couponCode,
      minOrderAmount: Number(values.minOrderAmount),
      discountType: values.discountType?.value,
      discountValue: Number(values.discountValue),
      startsDate: values.startsDate,
      expiryDate: values.expiryDate,
      product: values.product?.value,
      maxCount: Number(values.maxCount),
      student: values.student?.value,
      ...productPayload,
      ...studentPayload,
    };
    updateCouponById({ id: selectedCouponId, body: payload }).then(
      (res: any) => {
        if (res?.error) {
          showToast("error", res?.error?.data?.message);
        } else {
          if (res?.data?.status) {
            showToast("success", res?.data?.message);
            resetForm();
            onClose();
          } else {
            showToast("error", res?.data?.message);
          }
        }
        setSubmitting(false);
      }
    );
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <CouponForm
              formikProps={formikProps}
              onClose={onClose}
              formType="EDIT"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditCouponWrapper;
